export const VILLAGE_EVENT = "C-Tribe Village Expo";
export const VILLAGE_DATES = "August 12-13, 2027";
export const VILLAGE_LOCATION = "Edmonton, AB. Venue to be announced.";
export const VILLAGE_APPLICATION_DEADLINE = "June 25, 2027";
export const VILLAGE_DECISION_DATE = "July 9, 2027";
export type VillageRecruitmentStage = "main_application" | "waitlist";
export function getVillageRecruitmentStage(now: Date = new Date()): VillageRecruitmentStage {
  return now.getTime() <= Date.parse("2027-06-25T23:59:59-06:00") ? "main_application" : "waitlist";
}

export const villageSegments = [
  "Employer", "Startup", "Community / training", "Marketplace", "Cultural booth", "Food vendor", "Partner",
] as const;

// Cultural booths can sell anything except food and drink; food sells through
// the separate food market. Both can be requested beyond Village Expo.
export const culturalBoothEvents = [
  "C-Tribe Village Expo (August 12-13, 2027)",
  "C-Tribe x Culture events (August 2027)",
  "C-Tribe x Fashion events (August 2027)",
  "C-Tribe x Fashion Show (February 25, 2027)",
] as const;

export const foodVendorEvents = [
  "C-Tribe Festival food market (August 2027, dates and location to be announced)",
  ...culturalBoothEvents,
] as const;
export const foodVendorTypes = [
  "Food truck", "Tent or booth with on-site preparation", "Pre-packaged food", "Drinks (non-alcoholic)", "Desserts and baked goods", "Other",
] as const;
export const foodPermitStatuses = [
  "Yes, I hold a current food handling permit or approval",
  "In progress",
  "Not yet, I would like guidance",
] as const;

// Zone names match the marketing site (C-Tribe repo, src/content/village-expo.ts VILLAGE_PACKAGES).
export const villagePackages = [
  "The Commons (community / training) - indicative CAD 199", "Builders' Row (startup) - indicative CAD 399",
  "Main Street (hiring employer) - indicative CAD 699", "The Corner Lot (featured employer) - indicative CAD 1,199",
  "The Village Market (marketplace) - indicative CAD 449", "Town Hall (partner) - custom / discuss with C-Tribe",
] as const;

// Mirrors the marketing site's industry map (VILLAGE_INDUSTRIES) so confirmed exhibitors can be filtered by it.
export const villageIndustries = [
  "Software and AI", "Games and interactive", "Film, TV, and digital media", "Fashion, music, and live events",
  "Energy and hydrogen", "Health and life sciences", "Agriculture and food", "Transportation and logistics",
  "AI, data centres, and quantum", "Clean energy and critical minerals", "Defence and aerospace",
  "Skilled trades and housing", "Other / not listed",
] as const;

export const villageDays = ["August 12, 2027", "August 13, 2027", "Not sure yet"] as const;
export const villageInterests = [
  "Career opportunities", "Startup and employer showcases",
  "Community and training programs", "Local marketplace",
  "Resume review", "Professional headshot",
] as const;

export const villageUpdatesConsent = "I agree to receive C-Tribe Village Expo event updates by email, managed by the C-Tribe team. This is an interest list, not an event ticket or admission approval. I can withdraw consent by emailing hello@ctribefestival.com. Read our <a href=\"https://www.ctribefestival.com/privacy\" class=\"text-blue-500\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Policy</a>.";
export const villageApplicationConsent = "I agree that C-Tribe may review this application and contact me about participating in C-Tribe Village Expo. Package prices are indicative CAD preferences only. Applying does not reserve a booth, confirm acceptance, or require payment. After June 25, 2027, submissions are for the waitlist only. Read our <a href=\"https://www.ctribefestival.com/privacy\" class=\"text-blue-500\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Policy</a>.";
export const resumeReviewConsent = "I authorise C-Tribe to review my career details and resume. Career introductions are not open. My employer-sharing preference is private: obtain my separate approval for each named employer before sharing my resume or contact details. This is not consent to automated matching or a guarantee of interviews, employment, or event admission. Read our <a href=\"https://www.ctribefestival.com/privacy\" class=\"text-blue-500\" target=\"_blank\" rel=\"noopener noreferrer\">Privacy Policy</a>.";

const packageHints: Record<string, { packagePreference: typeof villagePackages[number]; exhibitorSegment?: typeof villageSegments[number] }> = {
  community: { packagePreference: villagePackages[0], exhibitorSegment: "Community / training" },
  startup: { packagePreference: villagePackages[1], exhibitorSegment: "Startup" },
  employer: { packagePreference: villagePackages[2], exhibitorSegment: "Employer" },
  featured: { packagePreference: villagePackages[3], exhibitorSegment: "Employer" },
  marketplace: { packagePreference: villagePackages[4], exhibitorSegment: "Marketplace" },
  cultural: { packagePreference: villagePackages[4], exhibitorSegment: "Cultural booth" },
  food: { packagePreference: villagePackages[4], exhibitorSegment: "Food vendor" },
  partner: { packagePreference: villagePackages[5], exhibitorSegment: "Partner" },
};

export function getVillagePackagePrefill(hint: unknown): Record<string, string> {
  return typeof hint === "string" && Object.hasOwn(packageHints, hint) ? { ...packageHints[hint] } : {};
}

const commonVendorFields = [
  "intro", "exhibitorSegment", "businessName", "firstName", "lastName", "email",
  "phoneNumber", "city", "organizationWebsite", "primaryIndustry",
];
const segmentFields: Record<string, string[]> = {
  Employer: ["jobOpenings", "careersUrl"],
  Startup: ["startupSummary", "hiringPlans"],
  "Community / training": ["programDescription", "programAudience"],
  Marketplace: ["itemsDescription", "vendorAcknowledgement"],
  "Cultural booth": ["culturalShowcase", "boothEvents", "itemsDescription", "vendorAcknowledgement"],
  "Food vendor": ["foodVendorType", "foodOffering", "foodEvents", "foodPermitStatus"],
  Partner: ["partnershipGoals"],
};

// Keep historical fields in the review schema, but show only this intake's
// relevant questions to applicants. Unknown segments cannot unlock a branch.
export function selectVillageVendorFields<T>(fields: Record<string, T>, segment: unknown): Record<string, T> {
  const branch = typeof segment === "string" && Object.hasOwn(segmentFields, segment) ? segmentFields[segment] : [];
  const keys = [...commonVendorFields, ...branch, "participationDays", "packagePreference", "applicationConsent"];
  return Object.fromEntries(keys.filter((key) => Object.hasOwn(fields, key)).map((key) => [key, fields[key]]));
}

export function clearInactiveVillageAnswers(segment: unknown): Record<string, null> {
  const active = typeof segment === "string" && Object.hasOwn(segmentFields, segment) ? segmentFields[segment] : [];
  return Object.fromEntries(Object.values(segmentFields).flat().filter((key) => !active.includes(key)).map((key) => [key, null]));
}

export function getVillageConfirmation(slug: string, stage = getVillageRecruitmentStage()): string | undefined {
  if (slug === "village-interest") return `Your interest in ${VILLAGE_EVENT} (${VILLAGE_DATES}) has been saved for the C-Tribe team's manually managed updates. ${VILLAGE_LOCATION} This is not a ticket, admission confirmation, or automatic newsletter enrollment. Withdraw consent by emailing hello@ctribefestival.com.`;
  if (slug === "vendor") return `Your ${VILLAGE_EVENT} ${stage === "waitlist" ? "waitlist request" : "application"} has been received for ${VILLAGE_DATES}. ${VILLAGE_LOCATION} Main application deadline: ${VILLAGE_APPLICATION_DEADLINE}; decisions: ${VILLAGE_DECISION_DATE}. After the deadline, submissions are waitlist-only and subject to availability. No payment has been taken and no booth is reserved.`;
  if (slug === "resumeupload") return "Your career information has been received for C-Tribe review. Career introductions are not open. Your preference requires separate approval for each named employer before sharing. No automated matching, interview, employment, or admission is guaranteed.";
  return undefined;
}
